import api from './api';

/**
 * Service for user-scoped penalties
 * Any authenticated user can list their own penalties (as sanctioned or beneficiary)
 */
class PenaltyService {
  /**
   * List penalties involving the authenticated user
   * GET /api/v1/penalties/user/penalties
   */
  async listUser(params = {}) {
    const response = await api.get('/penalties/user/penalties', { params });
    return response.data;
  }

  /**
   * Get a single penalty by ID
   * GET /api/v1/penalties/:penaltyId
   */
  async get(penaltyId) {
    const response = await api.get(`/penalties/${penaltyId}`);
    return response.data;
  }

  // ═══════════════════════════════════════════════════════════
  // Admin methods
  // ═══════════════════════════════════════════════════════════

  /**
   * Statistiques agrégées (admin dashboard)
   * GET /api/v1/penalties/admin/stats
   */
  async getAdminStats() {
    const response = await api.get('/penalties/admin/stats');
    return response.data?.data || response.data;
  }

  /**
   * Liste paginée avec filtres (admin)
   * GET /api/v1/penalties
   */
  async adminList({
    status, penalty_type, source_type, user_id, reservation_id,
    dispute_id, search, page = 1, limit = 20, sort,
    victim_compensation_status
  } = {}) {
    const params = { page, limit };
    if (status)                    params.status = status;
    if (penalty_type)              params.penalty_type = penalty_type;
    if (source_type)               params.source_type = source_type;
    if (user_id)                   params.user_id = user_id;
    if (reservation_id)            params.reservation_id = reservation_id;
    if (dispute_id)                params.dispute_id = dispute_id;
    if (search)                    params.search = search;
    if (sort)                      params.sort = sort;
    if (victim_compensation_status) params.victim_compensation_status = victim_compensation_status;

    const response = await api.get('/penalties', { params });
    return response.data; // { success, data, pagination }
  }

  /**
   * Créer une pénalité manuelle (admin)
   * POST /api/v1/penalties
   */
  async adminCreate(payload) {
    const response = await api.post('/penalties', payload);
    return response.data?.data || response.data;
  }

  /**
   * Mettre à jour le statut
   * PATCH /api/v1/penalties/:penaltyId/status
   */
  async adminUpdateStatus(penaltyId, { status, message, dispute_reason, admin_notes }) {
    const response = await api.patch(`/penalties/${penaltyId}/status`, {
      status, message, dispute_reason, admin_notes
    });
    return response.data?.data || response.data;
  }

  /**
   * Ajouter un événement / note
   * POST /api/v1/penalties/:penaltyId/events
   */
  async adminAddEvent(penaltyId, { event_type = 'note', message, metadata }) {
    const response = await api.post(`/penalties/${penaltyId}/events`, {
      event_type, message, metadata
    });
    return response.data?.data || response.data;
  }

  /**
   * Déclencher la collecte monétaire (dépôt Stripe / déduction payout)
   * POST /api/v1/penalties/:penaltyId/apply
   */
  async adminApplyMonetary(penaltyId, { victim_user_id, victim_has_stripe } = {}) {
    const response = await api.post(`/penalties/${penaltyId}/apply`, {
      victim_user_id, victim_has_stripe
    });
    return response.data?.data || response.data;
  }

  /**
   * Marquer la victime comme indemnisée manuellement
   * POST /api/v1/penalties/:penaltyId/mark-compensated
   */
  async adminMarkCompensated(penaltyId, { compensation_method, compensation_reference, notes } = {}) {
    const response = await api.post(`/penalties/${penaltyId}/mark-compensated`, {
      compensation_method, compensation_reference, notes
    });
    return response.data?.data || response.data;
  }
}

export default new PenaltyService();

